import { useCallback, useEffect, useReducer } from "react";
import { useDebounce } from "use-debounce";

import { Customer } from "../../../../../modules/customers/api/customer.schema";
import { useToast } from "../../../../../hooks/useToast";
import { getCustomers } from "../../../../../modules/customers/api/customer";

interface ReducerState {
    isLoading: boolean;
    searchTerm: string;
    customers: Array<Customer>;
    dropdownOpen: boolean;
}

interface SearchTermAction {
    type: "searchTerm",
    payload: ReducerState["searchTerm"];
}

interface CustomerAction {
    type: "customers",
    payload: ReducerState["customers"];
}

interface DropdownOpenAction {
    type: "dropdownOpen",
    payload: ReducerState["dropdownOpen"],
}

type ReducerAction = SearchTermAction | CustomerAction | DropdownOpenAction;

const initialReducerState = {
    isLoading: false,
    searchTerm: "",
    customers: [],
    dropdownOpen: false,
}

function reducer(state: ReducerState, action: ReducerAction) {
    switch (action.type) {
        case "searchTerm":
            return {
                ...state,
                isLoading: action.payload ? true : false,
                dropdownOpen: action.payload ? true : false,
                searchTerm: action.payload,
            }
        case "dropdownOpen":
            return {
                ...state,
                dropdownOpen: action.payload,
            }
        case "customers":
            return {
                ...state,
                isLoading: false,
                customers: action.payload,
            }
        default:
            return state;
    }
}

export default function useSearchCustomers() {
    const [state, dispatch] = useReducer(reducer, initialReducerState);
    const [debouncedSearchTerm] = useDebounce(state.searchTerm, 500);

    const { addToast } = useToast();

    useEffect(() => {
        const updateData = async () => {
            if (!debouncedSearchTerm) {
                dispatch({
                    type: "customers",
                    payload: [],
                });
                return;
            }

            const response = await getCustomers({
                search: debouncedSearchTerm,
            });

            if (!response.success) {
                let message = "Unable to get customers.";
                message += response.message ? ` ${response.message}` : "";
                addToast(message, "error");
            }

            dispatch({
                type: "customers",
                payload: response?.data?.data || [],
            });
        }

        updateData();
    }, [debouncedSearchTerm, addToast]);

    const setDropdownOpen = useCallback((isOpen: ReducerState["dropdownOpen"]) => {
        dispatch({
            type: "dropdownOpen",
            payload: isOpen,
        });
    }, []);

    return {
        ...state,
        setDropdownOpen,
        setSearchTerm: (term: ReducerState["searchTerm"]) => {
            dispatch({
                type: "searchTerm",
                payload: term,
            });
        },
    }
}
